import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { message } from 'antd';
import {
  batches,
  qcApprove,
  dispensingList,
  updateDispensing,
  crete_testForm,
  testRequestCycles,
  closeTestRequest,
  retestDueBatches
} from './api';

const initialState = {
  batchdetails: [],
  dispenses: []
};

const actions = {
  GET_ALL_BATCHES: 'dispensing/GET_ALL_BATCHES',
  UPDATE_QC_APPROVE: 'dispensing/UPDATE_QC_APPROVE',
  GET_ALL_DISPENSES: 'dispensing/GET_ALL_DISPENSES',
  UPDATE_DISPENSES: 'dispensing/UPDATE_DISPENSES',
  CREATE_TEST_FORM: 'dispensing/CREATE_TEST_FORM',
  GET_TEST_REQUEST_CYCLES: 'qa/GET_TEST_REQUEST_CYCLES',
  CLOSE_TEST_REQUEST: 'qa/CLOSE_TEST_REQUEST'
};

export const getAllBatches = createAsyncThunk(
  actions.GET_ALL_BATCHES,
  async (payload) => {
    const response = await batches('get-all', payload);

    return response;
  }
);

export const updateQCapprove = createAsyncThunk(
  actions.UPDATE_QC_APPROVE,
  async (payload) => {
    const response = await qcApprove('bacth-qc', payload);

    return response;
  }
);

export const getAllDispenses = createAsyncThunk(
  actions.GET_ALL_DISPENSES,
  async (payload) => {
    const response = await dispensingList('get-all', payload);

    return response;
  }
);

export const updateDispense = createAsyncThunk(
  actions.UPDATE_DISPENSES,
  async (payload) => {
    const response = await updateDispensing('update', payload);

    return response;
  }
);

export const getRetestDueBatches = createAsyncThunk(
  'qa/retestDue',
  async (payload) => {
    const response = await retestDueBatches(payload);

    return response;
  }
);

export const createTestForm = createAsyncThunk(
  actions.CREATE_TEST_FORM,
  async (payload) => {
    // Route on whether THIS request has been raised yet — not on batch status
    // alone.
    //
    // Under multi-cycle the batch deliberately stays in Quarantine from the
    // moment a request is raised until its line clearance is approved, so
    // "batch is Quarantine" no longer implies "no request exists". Routing on it
    // sent QC's Update submit to create-trf, which persists none of the QC
    // fields, writes no test_request_form_logs row, and re-issues the PDF with
    // NA in every QC cell (createTestRequestForm hardcodes them).
    //
    //   no trf row                    → create-trf  raise the request
    //   trf row with no lifecycle yet → create-trf  pre-populated upstream by
    //                                   code_transfer.handleApprove, which sets
    //                                   is_trf_codetransfer and leaves
    //                                   request_status NULL; the status-flip
    //                                   side effects still have to fire
    //   trf row already raised        → update-trf  QC filling the form
    const isQuarantine = payload?.batch_qc_status === 'Quarantine';
    const isUnraised = !payload?.trf_request_status;
    /* Superseded routing, kept for reference:
         payload?.type == 'regenerate' ? 'regenerate_trf'
       : isQuarantine                  ? 'create-trf'
       : payload?.trf_id               ? 'update-trf'
       : 'create-trf'
    */
    const response = await crete_testForm(
      `${
        payload?.type == 'regenerate'
          ? 'regenerate_trf'
          : !payload?.trf_id
          ? 'create-trf'
          : isQuarantine && isUnraised
          ? 'create-trf'
          : 'update-trf'
      }`,
      payload
    );

    return response;
  }
);

export const getTestRequestCycles = createAsyncThunk(
  actions.GET_TEST_REQUEST_CYCLES,
  async (payload) => {
    const response = await testRequestCycles(payload);

    return response;
  }
);

export const closeTestRequestCycle = createAsyncThunk(
  actions.CLOSE_TEST_REQUEST,
  async (payload) => {
    const response = await closeTestRequest(payload);

    return response;
  }
);

export const qaapprovalSlice = createSlice({
  name: 'qaapproval',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getAllBatches.pending, (state) => {
        state.getAllMaterilLoading = true;
      })
      .addCase(getAllBatches.fulfilled, (state, action) => {
        state.getAllMaterilLoading = false;

        const { success, data, pagination, message: msg } = action.payload;
        if (success) {
          state.batchdetails = data;
          state.pagination = pagination || null;
        } else {
          state.batchdetails = [];
          message.error(msg);
        }
      })
      .addCase(getAllBatches.rejected, (state, action) => {
        const { message: msg } = action.error;

        state.getAllMaterilLoading = false;

        message.error(msg);
      });
    builder
      .addCase(updateQCapprove.pending, (state) => {
        state.getAllMaterilLoading = true;
      })
      .addCase(updateQCapprove.fulfilled, (state, action) => {
        state.getAllMaterilLoading = false;

        const { success, message: msg } = action.payload;
        if (!success) {
          message.error(msg);
        }
      })
      .addCase(updateQCapprove.rejected, (state, action) => {
        const { message: msg } = action.error;

        state.getAllMaterilLoading = false;

        message.error(msg);
      });
    builder
      .addCase(getAllDispenses.pending, (state) => {
        state.getAllMaterilLoading = true;
      })
      .addCase(getAllDispenses.fulfilled, (state, action) => {
        state.getAllMaterilLoading = false;

        const { success, data, pagination } = action.payload;
        if (success) {
          state.dispenses = data;
          state.pagination = pagination || null;
        } else {
          state.dispenses = [];
        }
      })
      .addCase(getAllDispenses.rejected, (state, action) => {
        const { message: msg } = action.error;

        state.getAllMaterilLoading = false;

        message.error(msg);
      });

    builder
      .addCase(updateDispense.pending, (state) => {
        state.getAllMaterilLoading = true;
      })
      .addCase(updateDispense.fulfilled, (state, action) => {
        state.getAllMaterilLoading = false;

        const { success, message: msg } = action.payload;
        if (!success) {
          message.error(msg);
        }
      })
      .addCase(updateDispense.rejected, (state, action) => {
        const { message: msg } = action.error;

        state.getAllMaterilLoading = false;

        message.error(msg);
      });

    builder
      .addCase(createTestForm.pending, (state) => {
        state.getAllMaterilLoading = true;
      })
      .addCase(createTestForm.fulfilled, (state, action) => {
        state.getAllMaterilLoading = false;

        const { success, message: msg } = action.payload;
        if (!success) {
          message.error(msg);
        }
      })
      .addCase(createTestForm.rejected, (state, action) => {
        const { message: msg } = action.error;

        state.getAllMaterilLoading = false;

        message.error(msg);
      });
  }
});
export default qaapprovalSlice.reducer;
